import { Device } from '@twilio/voice-sdk';

// PROD-9012. Two SDK constraints this module exists to satisfy:
//
// 1. The SDK's tones (ringback, DTMF, disconnect) play through ONE AudioContext
//    shared by all Devices, created in the Device constructor — at page load
//    here, never in a user gesture, so Safari leaves it suspended until a
//    resume() that runs inside a user activation. It is reachable only as the
//    static `Device.audioContext`; AudioHelper's copy is a private
//    `_audioContext` with no getter, so `device.audio.audioContext` is always
//    undefined.
//
// 2. Safari has no HTMLAudioElement.setSinkId, so the SDK falls back to a
//    `new Audio()` that it never inserts into the document and plays from the
//    ontrack handler. WebKit will not start that element, so we adopt the one
//    it hands us via `call.on('audio')`.

const HOST_ID = 'twilio-remote-audio';

// Remote audio elements for calls that are still live, mapped to the
// caller's play-failure reporter. The gesture handler below retries playback on
// these, so a first play() that Safari refused recovers on the agent's next
// click instead of staying silent for the call.
const liveRemoteAudio = new Map();

let gestureUnlockInstalled = false;

// A 'closed' context can never be resumed — resume() rejects with
// InvalidStateError — and only a new Device rebuilds the shared one. Report the
// state instead of throwing so the caller can record it on the call.
export const getSharedContextState = () => {
  try {
    return Device.audioContext?.state ?? null;
  } catch (e) {
    return null;
  }
};

const resumeSharedContext = () => {
  const ctx = Device.audioContext;
  if (!ctx) return;
  if (ctx.state === 'closed') {
    // eslint-disable-next-line no-console
    console.warn('twilio_audio.context_closed');
    return;
  }
  if (ctx.state !== 'suspended') return;
  const resumed = ctx.resume();
  if (resumed && typeof resumed.catch === 'function') {
    resumed.catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('twilio_audio.resume_failed', err?.name || err);
    });
  }
};

const playRemoteAudio = (el, onPlayFailed) => {
  const played = el.play?.();
  if (played && typeof played.catch === 'function') {
    played.catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('twilio_audio.remote_play_blocked', err?.name || err);
      try {
        onPlayFailed?.(err);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('twilio_audio.play_failed_callback_threw', e);
      }
    });
  }
};

const onUserGesture = () => {
  resumeSharedContext();
  liveRemoteAudio.forEach((onPlayFailed, el) => {
    if (el.paused) playRemoteAudio(el, onPlayFailed);
  });
};

// Kept for the life of the page rather than removed after the first success:
// Safari re-suspends the context when the tab is hidden or an OS audio
// interruption lands, so a single unlock is not enough.
const installGestureUnlock = () => {
  if (gestureUnlockInstalled || typeof document === 'undefined') return;
  gestureUnlockInstalled = true;
  const opts = { capture: true, passive: true };
  document.addEventListener('pointerdown', onUserGesture, opts);
  document.addEventListener('keydown', onUserGesture, opts);
};

const getAudioHost = () => {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.setAttribute('aria-hidden', 'true');
    host.style.position = 'absolute';
    host.style.width = '0';
    host.style.height = '0';
    host.style.overflow = 'hidden';
    document.body.appendChild(host);
  }
  return host;
};

// Call this synchronously from the click that starts or answers a call — that
// is the only moment Safari will honour the resume.
export const unlockTwilioAudio = () => {
  installGestureUnlock();
  try {
    resumeSharedContext();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('twilio_audio.unlock_failed', e);
  }
};

// Adopt the remote-media element the SDK creates for `call`. Returns a detach
// function the caller must run when the call reaches a terminal state.
export const attachRemoteAudio = (call, { onPlayFailed } = {}) => {
  if (!call || typeof call.on !== 'function' || typeof document === 'undefined') {
    return () => {};
  }
  installGestureUnlock();
  const adopted = [];

  const onAudio = (el) => {
    if (!el) return;
    // Attributes, not properties: the element is the SDK's, and assigning to a
    // parameter's props trips the repo's lint rules.
    el.setAttribute('autoplay', '');
    el.setAttribute('playsinline', '');
    try {
      getAudioHost().appendChild(el);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('twilio_audio.remote_attach_failed', e);
    }
    adopted.push(el);
    liveRemoteAudio.set(el, onPlayFailed);
    playRemoteAudio(el, onPlayFailed);
  };

  call.on('audio', onAudio);

  return () => {
    if (typeof call.removeListener === 'function') call.removeListener('audio', onAudio);
    adopted.forEach((el) => {
      liveRemoteAudio.delete(el);
      // Leave pause()/srcObject teardown to the SDK, which owns the element;
      // detaching it from the DOM is enough to stop it being played.
      el.remove?.();
    });
    adopted.length = 0;
  };
};
